import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../services/api";
import { activeTab, blockReason, useConsole } from "../lib/console";
import { usePoll } from "../lib/hooks";
import { duration, fmtDateTime, num, plural } from "../lib/format";
import { Badge, Button, Callout, Card, Empty, ErrorState, Modal, SearchInput, SevBadge, SkeletonRows, Stat, cx } from "../components/ui";
import { Icon } from "../components/Icon";
import { PageHeader, PageTabs } from "../components/ConsoleShell";

// ── Policy explainer ────────────────────────────────────────────
const POLICY = [
  { icon: "zap", title: "High or critical detection", body: "The source is blocked immediately for 8 minutes." },
  { icon: "refresh", title: "Repeat offender", body: "4 or more confirmed detections from one source within 10 minutes blocks it for 8 minutes." },
  { icon: "sparkle", title: "Anomalies alone", body: "Behavioural anomalies without a known signature never block on their own." },
  { icon: "lock", title: "Manual and honeypot blocks", body: "Stay in place until an analyst releases them." },
];

function Policy() {
  return (
    <Card title="Response policy" subtitle="How the platform decides to block" icon="shield">
      <div className="grid cols-4" style={{ gap: 12 }}>
        {POLICY.map((p) => (
          <div key={p.title} className="card-inset" style={{ display: "flex", gap: 12 }}>
            <span className="stat-icon tone-brand" style={{ flexShrink: 0 }}>
              <Icon name={p.icon} size={15} />
            </span>
            <div>
              <div className="strong small" style={{ color: "var(--text)" }}>
                {p.title}
              </div>
              <div className="small muted" style={{ marginTop: 2 }}>
                {p.body}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Blocked sources ─────────────────────────────────────────────
function Sources({ rules }) {
  const c = useConsole();
  const [q, setQ] = useState("");
  const [release, setRelease] = useState(null);
  const [busy, setBusy] = useState(false);

  const list = useMemo(() => {
    const items = rules.data?.rules || [];
    const n = q.trim().toLowerCase();
    return n ? items.filter((r) => `${r.ip} ${r.reason} ${r.severity}`.toLowerCase().includes(n)) : items;
  }, [rules.data, q]);

  const doRelease = async () => {
    setBusy(true);
    await c.allowIP(release.ip);
    setBusy(false);
    setRelease(null);
    rules.reload();
  };

  return (
    <Card flush>
      <div className="table-toolbar">
        <SearchInput value={q} onChange={setQ} placeholder="Filter by IP or reason" label="Filter blocked sources" />
        <div className="tb-spacer" />
        <span className="small muted">{rules.data ? plural(rules.data.total ?? list.length, "rule") : ""}</span>
        <Button size="sm" variant="ghost" icon="refresh" onClick={rules.reload}>
          Refresh
        </Button>
      </div>
      {rules.error ? (
        <ErrorState detail={rules.error} onRetry={rules.reload} />
      ) : !rules.data ? (
        <div className="card-body">
          <SkeletonRows rows={5} />
        </div>
      ) : list.length === 0 ? (
        q ? (
          <Empty icon="filter" title="No blocked source matches" />
        ) : (
          <Empty icon="shield" tone="ok" title="No sources are blocked right now">
            Blocks appear here as soon as the response policy or an analyst stops a source.
          </Empty>
        )
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Why</th>
                <th>Severity</th>
                <th className="table-hide-sm">Blocked at (GST)</th>
                <th>Expires</th>
                <th className="num table-hide-sm">Dropped after</th>
                <th className="table-hide-sm">Enforced at</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((r) => {
                const why = blockReason(r.reason);
                return (
                  <tr key={r.ip}>
                    <td>
                      <button type="button" className="mono" style={{ border: 0, background: "none", color: "var(--brand)", padding: 0, fontSize: 12.5 }} onClick={() => c.openProfile(r.ip)}>
                        {r.ip}
                      </button>
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{why.label}</div>
                      <div className="tiny muted">{why.detail}</div>
                    </td>
                    <td>
                      <SevBadge sev={r.severity} />
                    </td>
                    <td className="table-hide-sm mono muted nowrap" style={{ fontSize: 12.5 }}>
                      {fmtDateTime(r.timestamp)}
                    </td>
                    <td className="nowrap">{r.expires_in_s != null ? <Badge tone="neutral">in {duration(r.expires_in_s)}</Badge> : <Badge tone="brand">Until released</Badge>}</td>
                    <td className="num table-hide-sm">{num(r.dropped_events)}</td>
                    <td className="table-hide-sm">
                      {r.applied ? (
                        <Badge tone="ok" title="An iptables rule exists on the host">
                          Host firewall
                        </Badge>
                      ) : (
                        <Badge tone="neutral" title="Dropped inside the detection pipeline (simulated sources never touch the host firewall)">
                          Pipeline
                        </Badge>
                      )}
                    </td>
                    <td className="num">
                      <Button size="sm" variant="ghost" icon="unlock" onClick={() => setRelease(r)}>
                        Release
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Modal open={!!release} onClose={() => setRelease(null)} label="Release source">
        <div className="modal-head">
          <h2>Release {release?.ip}?</h2>
          <p>Traffic from this source will reach the platform again. A new high-severity detection will block it again automatically.</p>
        </div>
        <div className="modal-foot" style={{ marginTop: 16 }}>
          <Button variant="ghost" onClick={() => setRelease(null)}>
            Cancel
          </Button>
          <Button variant="success" icon="unlock" loading={busy} onClick={doRelease} data-autofocus>
            Release
          </Button>
        </div>
      </Modal>
    </Card>
  );
}

// ── Blocked accounts ────────────────────────────────────────────
function Accounts() {
  const c = useConsole();
  const users = [...c.blockedUsers];
  return (
    <div className="stack">
      <Callout tone="info" icon="info">
        Accounts are blocked from the demo target site's sign-in after brute-force attempts against them. Block an account from a failed-login detection; restore it here.
      </Callout>
      <Card flush title="Blocked accounts" subtitle={plural(users.length, "account")} icon="lock">
        {users.length === 0 ? (
          <Empty icon="user" tone="ok" title="No blocked accounts">
            Open a failed-login detection in the live feed to block the targeted account.
          </Empty>
        ) : (
          <div className="list">
            {users.map((u) => (
              <div key={u} className="list-item" style={{ gridTemplateColumns: "auto minmax(0,1fr) auto" }}>
                <span className="sev-glyph tone-high">
                  <Icon name="lock" size={15} />
                </span>
                <span className="list-title">@{u}</span>
                <Button size="sm" variant="success" icon="unlock" onClick={() => c.allowUser(u)}>
                  Restore
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ── DQN response agent ──────────────────────────────────────────
const ACTION_TONE = { IGNORE: "info", ALERT: "brand", THROTTLE: "medium", BLOCK: "high", QUARANTINE: "critical" };
const ACTION_HELP = {
  IGNORE: "No action: the activity looks routine.",
  ALERT: "Notify an analyst; traffic is not changed.",
  THROTTLE: "Rate-limit the source while it is reviewed.",
  BLOCK: "Block the source at the firewall.",
  QUARANTINE: "Isolate the affected host and block the source.",
};
const FEATURES = [
  { key: "severity", label: "Detection severity", hint: "How serious the detected activity is" },
  { key: "anomaly", label: "Anomaly score", hint: "How unusual versus normal behaviour" },
  { key: "threat", label: "Threat-intel match", hint: "Source appears on threat feeds" },
  { key: "ip_repeat", label: "Repeat source rate", hint: "Same source seen repeatedly" },
  { key: "failed_logins", label: "Failed logins", hint: "Authentication failures from the source" },
  { key: "ports_scanned", label: "Ports scanned", hint: "Breadth of ports probed" },
  { key: "mitre_active", label: "MITRE techniques active", hint: "ATT&CK techniques observed" },
  { key: "time_of_day", label: "Time of day", hint: "0 = midnight, 0.5 = midday", clock: true },
];
const SCENARIOS = [
  { name: "Routine login", state: [0.05, 0.05, 0.05, 0.0, 0.05, 0.0, 0.0, 0.5] },
  { name: "Single port scan", state: [0.35, 0.3, 0.3, 0.2, 0.0, 0.6, 0.3, 0.5] },
  { name: "SSH brute force", state: [0.65, 0.5, 0.6, 0.6, 0.9, 0.1, 0.5, 0.2] },
  { name: "SQL injection", state: [0.85, 0.6, 0.75, 0.4, 0.1, 0.1, 0.7, 0.5] },
  { name: "Active ransomware", state: [1.0, 0.9, 0.95, 0.7, 0.2, 0.3, 0.95, 0.1] },
];

export function useDLMetrics() {
  const [state, setState] = useState({ data: null, error: null });
  useEffect(() => {
    let timer;
    let alive = true;
    const refresh = async () => {
      try {
        const r = await api.get("/api/dl/metrics");
        if (!alive) return;
        setState({ data: r.data, error: null });
        timer = setTimeout(refresh, r.data.status === "training" ? 3000 : 30000);
      } catch (e) {
        if (!alive) return;
        setState((s) => ({ data: s.data, error: s.data ? null : e?.response?.data?.detail || e.message }));
        timer = setTimeout(refresh, 8000);
      }
    };
    refresh();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, []);
  return state;
}

function Agent() {
  const { data, error } = useDLMetrics();
  const [state, setState] = useState(SCENARIOS[2].state);
  const [scenario, setScenario] = useState(SCENARIOS[2].name);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const seq = useRef(0);
  const ready = data?.status === "ready" && data?.dqn?.trained;

  // Re-evaluate (debounced) whenever an input changes
  useEffect(() => {
    if (!ready) return undefined;
    const id = ++seq.current;
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/api/dl/dqn-action", { state });
        if (id === seq.current) {
          setResult(r.data);
          setErr(null);
        }
      } catch {
        if (id === seq.current) setErr("The response agent is unavailable.");
      }
    }, 160);
    return () => clearTimeout(t);
  }, [state, ready]);

  if (error) return <ErrorState detail={error} />;
  if (!data)
    return (
      <Card>
        <SkeletonRows rows={6} />
      </Card>
    );
  if (!data.torch_available)
    return (
      <Card>
        <Empty icon="cpu" title="Deep learning is disabled on this server">
          PyTorch is not installed, so the response agent cannot run.
        </Empty>
      </Card>
    );

  const actions = data.dqn?.actions || Object.keys(ACTION_TONE);
  const q = result?.q_values?.length ? result.q_values : null;
  const qMin = q ? Math.min(...q) : 0;
  const qMax = q ? Math.max(...q) : 1;
  const tone = ACTION_TONE[result?.action] || "brand";
  const set = (i, v) => {
    setScenario("Custom");
    setState((s) => s.map((x, k) => (k === i ? v : x)));
  };

  return (
    <div className="stack">
      <Card
        title="Response agent console"
        subtitle="Describe an incident and the DQN agent recommends a response. Results update as you move any input."
        icon="cpu"
      >
        <div className="row row-wrap" style={{ marginBottom: 18 }} role="group" aria-label="Example incidents">
          {SCENARIOS.map((s) => (
            <button
              key={s.name}
              type="button"
              className={cx("chip", scenario === s.name && "is-active")}
              aria-pressed={scenario === s.name}
              onClick={() => {
                setScenario(s.name);
                setState(s.state);
              }}
            >
              {s.name}
            </button>
          ))}
          {scenario === "Custom" && <Badge tone="brand">Custom</Badge>}
        </div>
        <div className="grid cols-12">
          <div className="span-7" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px 22px", alignContent: "start" }}>
            {FEATURES.map((f, i) => (
              <label key={f.key} className="field" title={f.hint}>
                <span className="row-between">
                  <span className="field-label">{f.label}</span>
                  <span className="mono small strong">{f.clock ? `${String(Math.round(state[i] * 24) % 24).padStart(2, "0")}:00` : state[i].toFixed(2)}</span>
                </span>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={state[i]}
                  onChange={(e) => set(i, parseFloat(e.target.value))}
                  style={{ width: "100%", accentColor: "var(--brand)" }}
                />
                <span className="field-hint">{f.hint}</span>
              </label>
            ))}
          </div>
          <div className="span-5">
            <div className="card-inset" style={{ minHeight: 280 }}>
              {!ready ? (
                <Empty compact icon="clock" title="The agent is still training">
                  {data.stage || "This takes under a minute after the backend starts."}
                </Empty>
              ) : err ? (
                <ErrorState title={err} detail="Try again in a moment." />
              ) : !result ? (
                <SkeletonRows rows={5} />
              ) : (
                <>
                  <div className="eyebrow">Recommended response</div>
                  <div className="row" style={{ margin: "6px 0 4px", gap: 12 }}>
                    <span style={{ fontSize: 26, fontWeight: 600, letterSpacing: "-0.02em", color: `var(--${tone === "brand" ? "brand" : `sev-${tone}`})` }}>{result.action}</span>
                    {result.risk_index != null && <Badge tone="neutral">risk index {Number(result.risk_index).toFixed(2)}</Badge>}
                  </div>
                  <p className="small dim">{ACTION_HELP[result.action]}</p>
                  {q && (
                    <div style={{ marginTop: 16 }}>
                      <div className="small muted" style={{ marginBottom: 8 }}>
                        Expected value of each response
                      </div>
                      {actions.map((a, i) => {
                        const w = ((q[i] - qMin) / Math.max(qMax - qMin, 0.001)) * 100;
                        const chosen = a === result.action;
                        const t = ACTION_TONE[a];
                        return (
                          <div key={a} className="q-bar">
                            <span style={{ fontWeight: chosen ? 600 : 400, color: chosen ? "var(--text)" : "var(--text-2)" }}>
                              {chosen && "▸ "}
                              {a}
                            </span>
                            <div className="meter" style={{ height: 6 }}>
                              <i style={{ width: `${Math.max(w, 2)}%`, background: `var(--${t === "brand" ? "brand" : `sev-${t}`})`, opacity: chosen ? 1 : 0.45 }} />
                            </div>
                            <span className="mono small" style={{ textAlign: "right" }}>
                              {q[i].toFixed(2)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {result.drivers?.length > 0 && (
                    <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--line)" }}>
                      <div className="small muted" style={{ marginBottom: 8 }}>
                        What drove this decision
                      </div>
                      {result.drivers.map((d) => {
                        const f = FEATURES.find((x) => x.key === d.feature);
                        return (
                          <div key={d.feature} className="row-between small" style={{ padding: "2px 0" }}>
                            <span className="dim">{f ? f.label : d.feature}</span>
                            <span className="mono" style={{ color: d.impact >= 0 ? "var(--sev-high)" : "var(--ok)" }}>
                              {d.impact >= 0 ? "raises" : "lowers"} value {Math.abs(d.impact).toFixed(2)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </Card>
      <Callout tone="info" icon="info">
        The agent is trained offline in a simulated response environment, where blocking a real attack earns a reward and blocking benign traffic is penalised. It always returns its
        single best action (no random exploration). It advises; it does not change live firewall rules.
      </Callout>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────
export default function Response() {
  const c = useConsole();
  const tab = activeTab("response", c.tab);
  const rules = usePoll("/api/firewall/rules", { interval: 10000 });
  const total = rules.data?.total ?? c.blockedIPs.size;
  const autoCount = (rules.data?.rules || []).filter((r) => r.reason !== "manual").length;

  return (
    <>
      <PageHeader
        eyebrow="Protect"
        icon="shield"
        title="Response"
        description="Review containment actions and manage blocked sources and accounts."
      />
      <div className="grid cols-3" style={{ marginBottom: 16 }}>
        <Stat label="Sources blocked now" icon="ban" tone="ok" value={num(total)} sub={rules.data ? `${autoCount} by policy · ${total - autoCount} by analysts` : " "} />
        <Stat
          label="Attempts dropped after blocking"
          icon="shield"
          tone="brand"
          value={num(rules.data?.dropped_events ?? c.stats?.dropped_events ?? 0)}
          sub="Events refused before analysis"
        />
        <Stat label="Accounts blocked" icon="lock" tone="high" value={num(c.blockedUsers.size)} sub="On the demo target sign-in" />
      </div>
      <Policy />
      <div style={{ marginTop: 20 }}>
        <PageTabs page="response" counts={{ sources: total, accounts: c.blockedUsers.size }} />
        {tab === "sources" && <Sources rules={rules} />}
        {tab === "accounts" && <Accounts />}
        {tab === "agent" && <Agent />}
      </div>
    </>
  );
}
